import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import {
  ThemeProvider,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment,
  People,
  QuestionMark,
  AccountCircle,
  Logout as LogoutIcon,
  VpnKey,
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/Dashboard';
import TalentAssignmentManager from './components/TalentAssignmentManager';
import Questions from './components/QuestionsSimple';
import ViewQuestions from './components/ViewQuestions';
import SectionsManagement from './components/SectionsManagement';
import ExamInstructions from './components/exam/ExamInstructions';
import SectionBasedExamInterface from './components/exam/SectionBasedExamInterface';
import ExamComplete from './components/exam/ExamComplete';
import UserProfile from './components/profile/UserProfile';
import ApiKeyManagement from './components/apikeys/ApiKeyManagement';
import { modernTheme } from './theme/modernTheme';
import './App.css';
import './styles/modern-global.css';

// Placeholder components
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <Box p={4} textAlign="center">
    <Typography variant="h3" fontWeight={800} className="gradient-text" mb={2}>
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      This page will be implemented based on your requirements.
    </Typography>
  </Box>
);

const Navigation: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // Hide navbar on exam instructions, interface, and complete pages, or on auth pages
  const isExamPage = location.pathname.match(/^\/exam\/[^/]+\/(instructions|interface|complete)$/);
  const isAuthPage = location.pathname.match(/^\/(login|register|forgot-password)$/);

  if (isExamPage || isAuthPage) {
    return null;
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
  };

  const getUserInitials = (): string => {
    if (!user) return '?';
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #14b8a6 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px 0 rgba(99, 102, 241, 0.2)',
      }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          📚 Gappeo Assessment Portal
        </Typography>

        {isAuthenticated ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              <Button
                component={Link}
                to="/dashboard"
                color="inherit"
                startIcon={<DashboardIcon />}
                variant={location.pathname === '/dashboard' ? 'outlined' : 'text'}
                sx={{
                  color: 'white',
                  borderColor: location.pathname === '/dashboard' ? 'white' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Dashboard
              </Button>
              <Button
                component={Link}
                to="/talentwise-assignment"
                color="inherit"
                startIcon={<People />}
                variant={location.pathname === '/talentwise-assignment' ? 'outlined' : 'text'}
                sx={{
                  color: 'white',
                  borderColor: location.pathname === '/talentwise-assignment' ? 'white' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Assignments
              </Button>
              <Button
                component={Link}
                to="/questions"
                color="inherit"
                startIcon={<QuestionMark />}
                variant={location.pathname === '/questions' ? 'outlined' : 'text'}
                sx={{
                  color: 'white',
                  borderColor: location.pathname === '/questions' ? 'white' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                }}
              >
                Questions
              </Button>
            </Box>

            {/* User Menu */}
            <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  width: 40,
                  height: 40,
                }}
              >
                {getUserInitials()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Box px={2} py={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {user?.first_name || user?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <MenuItem
                component={Link}
                to="/profile"
                onClick={handleMenuClose}
              >
                <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
                My Profile
              </MenuItem>
              <MenuItem
                component={Link}
                to="/api-keys"
                onClick={handleMenuClose}
              >
                <VpnKey sx={{ mr: 1.5, fontSize: 20 }} />
                API Keys
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              to="/login"
              color="inherit"
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white',
                }
              }}
            >
              Login
            </Button>
            <Button
              component={Link}
              to="/register"
              color="inherit"
              variant="contained"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                }
              }}
            >
              Register
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

function App() {
  return (
    <ThemeProvider theme={modernTheme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <div className="App">
            <Navigation />
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/talent-assignment"
                element={
                  <ProtectedRoute>
                    <PlaceholderPage title="Talent Assignment" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/talentwise-assignment"
                element={
                  <ProtectedRoute>
                    <TalentAssignmentManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/questions"
                element={
                  <ProtectedRoute>
                    <Questions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/view-questions"
                element={
                  <ProtectedRoute>
                    <ViewQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sections-management"
                element={
                  <ProtectedRoute>
                    <SectionsManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-keys"
                element={
                  <ProtectedRoute>
                    <ApiKeyManagement />
                  </ProtectedRoute>
                }
              />

              {/* Exam Routes (Public - anyone with assignment token can access) */}
              <Route path="/exam/:assignmentId/instructions" element={<ExamInstructions />} />
              <Route path="/exam/:assignmentId/interface" element={<SectionBasedExamInterface />} />
              <Route path="/exam/:assignmentId/complete" element={<ExamComplete />} />
            </Routes>
          </div>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
